import {
  Component,
  DoCheck, ElementRef,
  EventEmitter,
  Input,
  IterableDiffer,
  IterableDiffers,
  OnChanges,
  OnInit,
  Output, QueryList,
  SimpleChanges,
  TrackByFunction, ViewChild, ViewChildren
} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { EditDocumentClassDialogComponent } from 'src/app/dialogs/edit-document-class-dialog/edit-document-class-dialog.component';
import { EditDocumentLangDialogComponent } from 'src/app/dialogs/edit-document-lang-dialog/edit-document-lang-dialog.component';
import { Approval, Document } from 'src/app/models/document';
import { DocumentClass } from 'src/app/models/documentClass';
import { DocumentEvidence } from 'src/app/models/documentEvidence';
import { DocumentMetadata, DocumentMetadataAndEvidence, MetadataType } from 'src/app/models/documentMetadata';
import { AclService } from 'src/app/services/acl.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { TerritoryService } from 'src/app/services/territory.service';
import { SelectionModel } from "@angular/cdk/collections";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { Trademark } from 'src/app/models/trademark';

interface Metadata {
  type: MetadataType;
  title: string;
  hint: string;
  placeholder: string;
  multiple: boolean;
  entries: DocumentMetadata[]
}

export interface DeleteMetadataEvent {
  metadata: DocumentMetadata,
  /**
   * Specifying a list a subgroup of evidence for deletion will prevent metadata from being deleted.
   */
  evidences?: DocumentEvidence[]
}


@Component({
  selector: 'app-edit-document-metadata',
  templateUrl: './edit-document-metadata.component.html',
  styleUrls: ['./edit-document-metadata.component.scss']
})
export class EditDocumentMetadataComponent implements OnInit, OnChanges {
  @Input() document?: Document;

  @Input() evidences: DocumentEvidence[] = []

  @Input() metadata: DocumentMetadata[] = []

  @Input() productsServices?: {
    ref: string;
    label?: string;
    classNumber: number;
    trademark: string;
  }[] = [];

  trademarks: Trademark[] = [];

  @Output() add = new EventEmitter<DocumentMetadataAndEvidence[]>();
  @Output() update = new EventEmitter<DocumentMetadataAndEvidence>();
  @Output() delete = new EventEmitter<DeleteMetadataEvent>();
  @Output() close = new EventEmitter<any>();
  @Output() toggleDraw = new EventEmitter<any>();
  @Output() confirmAnnotation = new EventEmitter<{ type: MetadataType, metadata: any }[]>();
  @Output() confirmWithoutZone = new EventEmitter<{ type: MetadataType, metadata: any }[]>();
  @Output() cancelAnnotation = new EventEmitter<any>();
  @Output() mouseenter = new EventEmitter<{
    type: MetadataType,
    value: any
  }>();
  @Output() mouseleave = new EventEmitter<{
    type: MetadataType,
    value: any
  }>();
  @Output() approveOrReject = new EventEmitter<any>();
  @Output() hoverEvidence = new EventEmitter<DocumentEvidence | null>()
  @Output() evidenceSelectionChange = new EventEmitter<boolean>();

  @Input() deleting: boolean = false;

  @ViewChildren('evidence')
  protected readonly evidence?: QueryList<ElementRef<HTMLElement>>;

  limits: { [type: string]: number } = {
    'TRADEMARK': 3,
    'LOCATION': 3,
    'LANG': 3,
    'CLASS': 1,
    'NICE_CLASS': 3,
    'PRODUCT_SERVICE': 3,
    'DATE': 3,
    'USER': 3
  };


  metadataEvidences: Metadata[] = [
    {
      type: MetadataType.CLASS,
      title: 'DOCUMENT.CLASS',
      hint: 'DOCUMENT.CLASS_HINT',
      placeholder: 'DOCUMENT.NO_CLASS',
      multiple: false,
      entries: []
    },
    {
      type: MetadataType.LANG,
      title: 'DOCUMENT.LANGS',
      hint: 'DOCUMENT.LANGS_HINT',
      placeholder: 'DOCUMENT.NO_LANG',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.TRADEMARK,
      title: 'ENTITIES.TRADEMARKS',
      hint: 'DOCUMENT.TRADEMARKS_HINT',
      placeholder: 'DOCUMENT.NO_TRADEMARKS',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.NICE_CLASS,
      title: 'DOCUMENT.NICE_CLASSIFICATION',
      hint: 'DOCUMENT.NICE_CLASSIFICATION_HINT',
      placeholder: 'DOCUMENT.NO_NICE_CLASSIFICATION',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.PRODUCT_SERVICE,
      title: 'DOCUMENT.PRODUCTS_SERVICES',
      hint: 'DOCUMENT.PRODUCTS_SERVICES_HINT',
      placeholder: 'DOCUMENT.NO_PRODUCTS_SERVICES',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.LOCATION,
      title: 'DOCUMENT.TERRITORIES',
      hint: 'DOCUMENT.TERRITORIES_HINT',
      placeholder: 'DOCUMENT.NO_TERRITORIES',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.DATE,
      title: 'DOCUMENT.PERIOD',
      hint: 'DOCUMENT.PERIOD_HINT',
      placeholder: 'DOCUMENT.NO_PERIOD',
      multiple: true,
      entries: []
    },
    {
      type: MetadataType.USER,
      title: 'DOCUMENT.USERS',
      hint: 'DOCUMENT.USERS_HINT',
      placeholder: 'DOCUMENT.NO_USERS',
      multiple: true,
      entries: []
    }
  ]

  public metadataType = MetadataType;

  // When true, the document is being viewed through a case: metadata falling
  // outside the case scope is rendered read-only (excluded) and TMs, P&S and
  // nice classes cannot be added, modified or deleted
  @Input() caseContext: boolean = false;
  // Territories of the trademarks of the document (EM shown as EU); all its territories when it has no trademark
  @Input() trademarkTerritories: string[] = [];

  @Input('isDraw') isDraw: boolean = false;
  // The user adds an information without pointing at a zone of the document
  addingWithoutZone: boolean = false;
  @Input('drawing') drawing?: {
    x: number,
    y: number,
    width: number,
    height: number
  };
  periodPattern: RegExp = /^[0-9]{4}-[0-9]{2}-[0-9]{2}_[0-9]{4}-[0-9]{2}-[0-9]{2}$/
  evidenceType: {
    type?: MetadataType,
    metadata?: any,
    period?: {
      start: moment.Moment,
      end: moment.Moment
    }
  } = {
      type: undefined,
      metadata: undefined
    };
  evidenceTypes: {
    type: MetadataType,
    metadata: any,
    period?: {
      start: moment.Moment,
      end: moment.Moment
    }
  }[] = []
  evidenceTypesOptions: {
    value: string,
    label: string,
    selected: boolean
  }[] = [
      {
        value: "TRADEMARK",
        label: "DOCUMENT.EVIDENCE_TYPE_TRADEMARK",
        selected: false
      },
      {
        value: "LOCATION",
        label: "DOCUMENT.EVIDENCE_TYPE_LOCATION",
        selected: false
      },
      {
        value: "DATE",
        label: "DOCUMENT.EVIDENCE_TYPE_DATE",
        selected: false
      },
      {
        value: "PERIOD",
        label: "DOCUMENT.EVIDENCE_TYPE_PERIOD",
        selected: false
      },
      {
        value: "NICE_CLASS",
        label: "DOCUMENT.EVIDENCE_TYPE_NICE_CLASS",
        selected: false
      },
      {
        value: "PRODUCT_SERVICE",
        label: "DOCUMENT.EVIDENCE_TYPE_PRODUCT_SERVICE",
        selected: false
      },
      {
        value: "USER",
        label: "DOCUMENT.EVIDENCE_TYPE_USER",
        selected: false
      }
    ];

  approval: 'approved' | 'rejected' | 'pending' = 'pending';

  /**
   * State management of metadata deletion.
   * Used to display the list of evidence to delete before sending the deletion request.
   */
  metadataPendingDeletion: DocumentMetadata | null = null
  evidencesPendingDeletion: DocumentEvidence[] = []
  selectedEvidences: SelectionModel<DocumentEvidence> = new SelectionModel(true, [], true, (e1: DocumentEvidence, e2: DocumentEvidence) => e1._id === e2._id)
  private _highlightedEvidence: DocumentEvidence | null = null

  trackEvidence: TrackByFunction<DocumentEvidence> = (index, item) => item._id ?? item

  constructor(
    private readonly _message: MessageService,
    private readonly aclService: AclService,
    private readonly dialog: MatDialog,
    private readonly documentService: DocumentService,
    private readonly territoryService: TerritoryService,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService
  ) { }


  get highlightedEvidence(): DocumentEvidence | null {
    return this._highlightedEvidence;
  }

  set highlightedEvidence(value: DocumentEvidence | null) {
    this._highlightedEvidence = value;
    this.hoverEvidence.emit(value);
  }

  ngOnInit(): void {
    moment.locale(this.translate.currentLang);
    this.populateMetadata();
    this.populateApproval();
  }


  ngOnChanges(changes: SimpleChanges): void {
    this.populateMetadata();
    this.populateApproval();
  }

  populateApproval() {
    if (this.document && this.document.approval) {
      this.approval = this.document.approval && this.document.approval.status ? this.document.approval.status : Approval.PENDING;
    }
  }

  populateMetadata() {
    this.metadataEvidences.forEach(metadatum => {
      metadatum.entries = this.metadata.filter(datum => (![MetadataType.DATE, MetadataType.PERIOD].includes(datum.type) && datum.type === metadatum.type) || ([MetadataType.DATE, MetadataType.PERIOD].includes(datum.type) && metadatum.type === MetadataType.DATE)).sort((a, b) => {
        if (metadatum.type === MetadataType.TRADEMARK) {
          return a.value.split('_')[1].localeCompare(b.value.split('_')[1]);
        } else if (metadatum.type === MetadataType.NICE_CLASS) {
          return parseInt(a.value) - parseInt(b.value);
        }
        return a.value.localeCompare(b.value);
      })
    })
  }


  humanFriendlyValue(type: MetadataType, entity: any) {
    switch (type) {
      case MetadataType.LANG:
        return this.translate.instant(`LANGUAGES.${entity.value}`)
      case MetadataType.LOCATION:
        return this.translate.instant(`TERRITORIES.${entity.value.toUpperCase()}`)
      case MetadataType.CLASS:
        return `${this.translate.instant(`DOCUMENT_CLASSES.${entity.value}`)}`;
      case MetadataType.TRADEMARK:
        return `${entity.value.split('_')[1]}`;
      case MetadataType.NICE_CLASS:
        return `${entity.value} - ${this.translate.instant(`NICE_CLASSES.${entity.value}`)}`;
      case MetadataType.DATE:
        moment.locale(this.translate.currentLang)
        return moment(entity.value).format("ll");
      case MetadataType.PERIOD:
        moment.locale(this.translate.currentLang)
        return `${moment(entity.value.split('_')[0]).format("ll")}_${moment(entity.value.split('_')[1]).format("ll")}`;
      case MetadataType.USER:
        return entity.value.split('_').join(' ');
    }
  }

  hasFilters(type: MetadataType): boolean {
    switch (type) {
      case MetadataType.LANG:
        return false;
      case MetadataType.LOCATION:
        return this.documentService.filters.territories && this.documentService.filters.territories.length > 0;
      case MetadataType.CLASS:
        return false;
      case MetadataType.TRADEMARK:
        return this.documentService.filters.trademark && this.documentService.filters.trademark.length > 0;
      case MetadataType.NICE_CLASS:
        return this.documentService.filters.classNumber && this.documentService.filters.classNumber.length > 0;
      case MetadataType.DATE:
      case MetadataType.PERIOD:
        return this.documentService.filters.period &&
          (this.documentService.filters.period.start !== undefined || this.documentService.filters.period.end !== undefined);
      case MetadataType.PRODUCT_SERVICE:
        return (this.documentService.filters.trademark && this.documentService.filters.trademark.length > 0)
          || (this.documentService.filters.classNumber && this.documentService.filters.classNumber.length > 0)
          || (this.documentService.filters.productsServices && this.documentService.filters.productsServices.length > 0);
      default:
        return false;
    }
  }

  getProductService(termId: string) {
    return this.productsServices?.find(ps => ps.ref === termId)?.label
  }

  getTrademark(trademarkId: string) {
    return this.document?.trademarks.find(t => t.ref === trademarkId)
  }

  /**
   * TMs, products & services and nice classes are linked to a case scope: they
   * must not be added, modified or deleted when the document is viewed through
   * a case
   */
  isCaseBlockedType(type: MetadataType): boolean {
    return this.caseContext && [
      MetadataType.TRADEMARK,
      MetadataType.NICE_CLASS,
      MetadataType.PRODUCT_SERVICE,
    ].includes(type);
  }

  /** A territory shown on the document that no trademark of the document covers, itself or through a parent territory. */
  isOutsideTrademarks(entry: DocumentMetadata): boolean {
    if (entry.type !== MetadataType.LOCATION || this.trademarkTerritories.length === 0) return false;
    const seen = new Set<string>();
    const pending = [entry.value];
    while (pending.length > 0) {
      const code = pending.pop()!;
      if (this.trademarkTerritories.includes(code)) return false;
      if (seen.has(code)) continue;
      seen.add(code);
      pending.push(...(this.territoryService.retrieveOne(code)?.parent ?? []));
    }
    return true;
  }

  isExcluded(entry: DocumentMetadata): boolean {
    return !!(this.caseContext && entry.excluded);
  }

  hasExcludedEntries(metadatum: Metadata): boolean {
    return this.caseContext && metadatum.entries.some(entry => entry.excluded);
  }

  onRemoveMetadata(type: MetadataType, entity: DocumentMetadata) {
    if (this.isCaseBlockedType(type) || this.deleting) return;
    this.enterEvidenceSelection(entity)
  }

  isValidateRemovalDisabled(type: MetadataType): boolean {
    if (this.deleting) return true;
    return this.selectedEvidences.selected.length === 0
      && ![MetadataType.CLASS, MetadataType.LANG].includes(type);
  }

  onValidateMetadataRemoval(type: MetadataType, entity: DocumentMetadata) {
    if (this.isCaseBlockedType(type) || this.isValidateRemovalDisabled(type)) return;
    this.delete.emit({
      metadata: entity,
      evidences: Array.from(this.selectedEvidences.selected)
    });
    this.exitEvidenceSelection()
  }

  onCancelMetadataRemoval() {
    this.exitEvidenceSelection()
  }

  onEvidenceToggle(evidence: DocumentEvidence) {
    this.selectedEvidences.toggle(evidence)
  }

  async editDocumentClass() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        document: this.document
      }
    }
    const dialog = this.dialog.open(EditDocumentClassDialogComponent, config);
    const result: { edited: boolean, class?: DocumentClass } = await firstValueFrom(dialog.afterClosed());
    if (result && result.edited && result.class && this.document) {
      const metadata = this.metadata.find(datum => datum.type === MetadataType.CLASS);
      if (metadata) {
        const metadataAndEvidence: DocumentMetadataAndEvidence = {
          _id: metadata?._id,
          type: metadata.type,
          value: DocumentClass[result.class]
        }
        this.update.emit(metadataAndEvidence)
      }
    }
  }

  async addDocumentLang() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        document: this.document
      }
    }
    const dialog = this.dialog.open(EditDocumentLangDialogComponent, config);
    const result: { edited: boolean, lang?: string } = await firstValueFrom(dialog.afterClosed());
    if (result && result.edited && result.lang && this.document) {
      const metadataAndEvidence: DocumentMetadataAndEvidence = {
        type: MetadataType.LANG,
        value: result.lang
      }
      this.add.emit([metadataAndEvidence])
    }
  }

  async editDocumentLang(lang: string) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        document: this.document,
        lang
      }
    }
    const dialog = this.dialog.open(EditDocumentLangDialogComponent, config);
    const result: { edited: boolean, lang?: string } = await firstValueFrom(dialog.afterClosed());
    if (result && result.edited && result.lang && this.document) {
      const metadata = this.metadata.find(datum => datum.type === MetadataType.LANG);
      if (metadata) {
        const metadataAndEvidence: DocumentMetadataAndEvidence = {
          _id: metadata?._id,
          type: metadata.type,
          value: result.lang
        }
        this.update.emit(metadataAndEvidence)
      }
    }
  }

  formatDate(date: string) {
    return moment(date).format('ll');
  }
  formatDateOrPeriod(entry: DocumentMetadata) {
    if (entry.type === MetadataType.PERIOD) {
      return `${this.formatDate(entry.value.split("_")[0])} - ${this.formatDate(entry.value.split("_")[1])}`
    }
    return moment(entry.value).format('ll');
  }
  formatDatetime(date: string) {
    return moment(date).format('lll');
  }

  onEvidenceTypePeriodChange(event: any) {
    if (!this.evidenceType.period) {
      this.evidenceType.period = {
        start: event.start,
        end: event.end
      }
    } else {
      this.evidenceType.period.start = event.start;
      this.evidenceType.period.end = event.end;
    }
    this.evidenceType.metadata = `${this.evidenceType.period.start ? this.evidenceType.period.start.format('YYYY-MM-DD') : ''}_${this.evidenceType.period.end ? this.evidenceType.period.end.format('YYYY-MM-DD') : ''}`
  }

  onEvidenceTypeTrademarksChange(event: any) {
    this.evidenceTypes = event.value.map((value: any) => ({
      type: MetadataType.TRADEMARK,
      metadata: value
    }))
  }

  onMouseEnter(type: MetadataType, value: DocumentMetadata['value']) {
    this.mouseenter.emit({ type, value });
  }

  onMouseLeave(type: MetadataType, value: DocumentMetadata['value']) {
    this.mouseleave.emit({ type, value });
  }

  onEvidencesMouseEnter(type: MetadataType, value: DocumentMetadata['value']) {
    this.onMouseEnter(type, value);
    this.highlightedEvidence = null;
  }

  onClose() {
    this.close.emit();
  }

  onToggleDraw() {
    this.addingWithoutZone = false;
    this.isDraw = !this.isDraw;
    this.toggleDraw.emit(this.isDraw)
  }

  onStartWithoutZone() {
    if (this.isDraw) {
      this.onToggleDraw();
    }
    this.addingWithoutZone = true;
  }

  isSelectingEvidenceType() {
    return (this.isDraw && !!this.drawing) || this.addingWithoutZone;
  }

  hasMetadataWithoutSupport() {
    return this.metadata.some(datum => datum.noEvidence);
  }

  isAnnotationDisabled() {
    return (this.evidenceType.type && ![MetadataType.TRADEMARK, MetadataType.PERIOD].includes(this.evidenceType.type) && !this.evidenceType.metadata)
      || (this.evidenceType.type === 'TRADEMARK' && this.evidenceTypes.some(evidenceType => !evidenceType.metadata._id))
      || (this.evidenceType.type === 'PERIOD' && (!this.evidenceType.period || !this.evidenceType.period?.start || !this.evidenceType.period.end || !this.periodPattern.test(this.evidenceType.metadata)));
  }

  private selectedEvidenceTypes(): { type: MetadataType, metadata: any }[] {
    if (this.evidenceType.type === MetadataType.TRADEMARK) {
      return this.evidenceTypes.map(evidenceType => ({
        type: evidenceType.type,
        metadata: evidenceType.metadata
      }));
    }
    return [{
      type: this.evidenceType.type as MetadataType,
      metadata: this.evidenceType.metadata
    }];
  }

  async onConfirmAnnotation() {
    if (!this.evidenceType.type) return;
    if (this.addingWithoutZone) {
      await this.confirmAddWithoutZone();
    } else if (this.isDraw && this.drawing) {
      this.confirmAnnotation.emit(this.selectedEvidenceTypes());
      this.onCancelAnnotation();
    }
  }

  // The purple box stays the recommended way: the user confirms before adding without a zone
  private async confirmAddWithoutZone() {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        title: 'DOCUMENT.ADD_WITHOUT_ZONE_CONFIRM_TITLE',
        text: 'DOCUMENT.ADD_WITHOUT_ZONE_CONFIRM_TEXT',
        button: {
          text: 'DOCUMENT.ADD_WITHOUT_ZONE_CONFIRM_BUTTON',
          class: 'main-button'
        }
      }
    }
    const dialog = this.dialog.open(ConfirmationDialogComponent, config);
    const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.confirmed) {
      this.confirmWithoutZone.emit(this.selectedEvidenceTypes());
      this.onCancelAnnotation();
    }
  }

  onCancelAnnotation() {
    this.addingWithoutZone = false;
    this.evidenceType = {
      type: undefined,
      metadata: undefined
    };
    this.evidenceTypes = [];
    this.evidenceTypesOptions.forEach((option) => option.selected = false);
    this.cancelAnnotation.emit();
  }

  async onApproveOrReject(event: MatRadioChange) {
    try {
      if (this.document && this.document._id) {
        this._message.emitChange("LOADING", "START");
        const document = await this.documentService.approveOrReject(this.document._id, event.value === "approved" ? Approval.APPROVED : Approval.REJECTED);
        this.toastr.success(this.translate.instant(event.value === "approved" ? 'DOCUMENT.DOCUMENT_APPROVED' : 'DOCUMENT.DOCUMENT_REJECTED', { name: this.document.name }));
        this.document.approval = document.approval;
        if (this.document.approval) {
          this.approval = this.document.approval && this.document.approval.status ? this.document.approval.status : Approval.PENDING;
        }
        this.approveOrReject.emit(document.approval);
        this._message.emitChange("LOADING", "END");
      }

    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  canApproveDocument() {
    return this.aclService.isManagerOrAboveOrCaseOperator();
  }

  public enterEvidenceSelection(metadata: DocumentMetadata) {
    const opening = this.metadataPendingDeletion === null;
    this.metadataPendingDeletion = metadata;
    this.evidencesPendingDeletion = this.evidences.filter(evidence => evidence.metadata === metadata._id)
    this.selectedEvidences.setSelection(...this.evidencesPendingDeletion)
    if (opening) {
      this.evidenceSelectionChange.emit(true);
    }
  }

  private exitEvidenceSelection() {
    const closing = this.metadataPendingDeletion !== null;
    this.metadataPendingDeletion = null;
    this.evidencesPendingDeletion = []
    this.selectedEvidences.clear()
    if (closing) {
      this.evidenceSelectionChange.emit(false);
    }
  }

  getTrademarks() {
    return this.document ? this.document.trademarks.map(t => ({
      _id: t.ref,
      identifierNumber: t.identifierNumber,
      name: t.name,
      countryOfDesignation: t.countryOfDesignation
    })) : [];
  }
}
